import { limit } from 'firebase/firestore';
import path from 'path';

const API_BASE_URL = process.env.REACT_APP_LP_API_BASE_URL || '';
const AUTH = process.env.REACT_APP_LP_AUTH || 'default_token';

type Payload = Record<string, string | number>;

interface ApiResponse {
  message: string;
  success: boolean;
  response?: any;
  data?: any;
}

export interface Request {
  path: string;
  payload?: Payload;
}

export const postData = async <T>(request: Request): Promise<ApiResponse> => {
  const queryParams = new URLSearchParams();

  if (request.payload) {
    for (const [key, value] of Object.entries(request.payload)) {
      queryParams.append(key, value.toString());
    }
  }

  const url = `${API_BASE_URL}/${request.path}?${queryParams.toString()}`;
  const options = {
    method: 'POST',
    headers: {
      Authorization: AUTH,
    },
  };

  try {
    const response = await fetch(url, options);

    if (!response.ok) {
      throw new Error(`Failed to fetch data for ${request.path}`);
    }

    const resultJson: ApiResponse = await response.json();
    return resultJson;
  } catch (error: any) {
    console.error(`Error fetching data for ${request.path}:`, error.message);
    throw error;
  }
};

export const getData = async <T>(request: Request): Promise<ApiResponse> => {
  const queryParams = new URLSearchParams();

  if (request.payload) {
    for (const [key, value] of Object.entries(request.payload)) {
      queryParams.append(key, value.toString());
    }
  }

  const url = `${API_BASE_URL}/${request.path}?${queryParams.toString()}`;
  const options = {
    method: 'GET',
    headers: {
      Authorization: AUTH,
    },
  };

  try {
    const response = await fetch(url, options);

    if (!response.ok) {
      throw new Error(`Failed to fetch data for ${request.path}`);
    }

    const resultJson: ApiResponse = await response.json();
    return resultJson;
  } catch (error: any) {
    console.error(`Error fetching data for ${request.path}:`, error.message);
    throw error;
  }
};

export const fetchAffiliates = async () => {
  try {
    const result = await getData<Request>({
      path: 'affiliates/getAll.do',
      payload: {
        limit: 1000,
      },
    });

    if (result.success && result.response.data.length > 0) {
      return result.response.data;
    }
  } catch (error: any) {
    console.error('Error fetching data:', error.message);
  }
};

export const fetchVerticals = async () => {
  try {
    const result = await getData<Request>({
      path: 'verticals/getAll.do',
    });

    if (result.success && result.response.data.length > 0) {
      return result.response.data;
    }
  } catch (error: any) {
    console.error('Error fetching data:', error.message);
  }
};

export const fetchCampaigns = async (
  verticalID: string,
  offerID?: string,
  start?: number,
  limit?: number,
  affiliateID?: number
) => {
  try {
    const payload: Record<string, string | number> = {
      verticalID,
      limit: limit ?? 1000,
      status: 'Active',
      start: start ?? 0,
    };
    if (offerID) {
      payload.offerID = offerID;
    }
    if (affiliateID) {
      payload.affiliateID = affiliateID;
    }

    const result = await getData<Request>({
      path: 'campaigns/getAll.do',
      payload,
    });

    if (result.success) {
      return result.response.data;
    }
  } catch (error: any) {
    console.error('Error fetching data:', error.message);
  }
};

export const fetchCampaignInfo = async (id: number) => {
  try {
    const result = await getData<Request>({
      path: 'campaigns/getBasicInfo.do',
      payload: {
        campaignID: id,
      },
    });

    if (result.success) {
      return result.data;
    }
  } catch (error: any) {
    console.error('Error fetching data:', error.message);
  }
};

export const fetchOfferInfo = async (offerId: number) => {
  try {
    const result = await getData<Request>({
      path: 'offers/getBasicInfo.do',
      payload: {
        offerID: offerId,
      },
    });

    if (result.success) {
      return result.data;
    }
  } catch (error: any) {
    console.error('Error fetching data:', error.message);
  }
};

export const fetchOffers = async (
  verticalID: string,
  advertiserId?: string
) => {
  try {
    const result = await getData<Request>({
      path: 'offers/getAll.do',
      payload: {
        verticalID,
        limit: 250,
        status: 'Active',
        revenueModel: 'Revenue Per Lead',
        payoutModel: 'Pay Per Lead',
        advertiserID: advertiserId ?? '',
      },
    });

    if (result.success) {
      return result.response.data;
    }
  } catch (error: any) {
    console.error('Error fetching data:', error.message);
  }
};

export const fetchContracts = async (
  lpType: string,
  verticalID: string,
  advertiserID?: string
) => {
  let path = 'leadDistributionContracts/getAll.do';
  if (lpType === 'calls') {
    path = 'callRoutingContracts/getAll.do';
  }

  const payload: Record<string, string | number> = {
    verticalID,
    status: 'Active',
  };

  if (advertiserID) {
    payload.advertiserID = parseInt(advertiserID);
  }

  try {
    const result = await getData<Request>({
      path: path,
      payload,
    });

    if (result.success && result.response.data.length > 0) {
      return result.response.data;
    }
  } catch (error: any) {
    console.error('Error fetching data:', error.message);
  }
};

export const fetchContractInfo = async (contractID: number, lpType: string) => {
  let path = 'leadDistributionContracts/getBasicInfo.do';
  if (lpType === 'calls') {
    path = 'callRoutingContracts/getBasicInfo.do';
  }

  try {
    const result = await getData<Request>({
      path: path,
      payload: {
        contractID,
      },
    });

    if (result.success) {
      return result.data;
    }
  } catch (error: any) {
    console.error('Error fetching data:', error.message);
  }
};

export const fetchSchedule = async (contractID: number) => {
  try {
    const result = await getData<Request>({
      path: 'leadDistributionContracts/getSchedule.do',
      payload: {
        contractID,
      },
    });

    if (result.success) {
      return result.response.data;
    }
  } catch (error: any) {
    console.error('Error fetching data:', error.message);
  }
};

export const setSchedulePrice = async (contractID: number, price: number) => {
  try {
    const result = await postData<Request>({
      path: 'leadDistributionContracts/setPrice.do',
      payload: {
        contractID,
        price,
      },
    });
    if (result.success) {
      return result;
    }
  } catch (error: any) {
    console.error('Error setting price:', error.message);
  }
};

// LP API POST Calls
//https://api.leadspedia.com/core/v2/contractFieldRevenueRules/create.do?contractID=22&fieldID=83&fieldValue=5&price=1

// LEAD RULES - GET CONTRACT FIELD REVENUE RULES
export const fetchContractLeadFieldRevenueRules = async (
  contractId: number
) => {
  try {
    const result = await getData<Request>({
      path: 'contractFieldRevenueRules/getAll.do',
      payload: {
        contractID: contractId,
      },
    });

    if (result.success) {
      return result.response.data;
    }
  } catch (error: any) {
    console.error('Error fetching data:', error.message);
  }
};

// LEAD RULES - CREATE CONTRACT FIELD REVENUE RULES
export const createContractLeadFieldRevenueRules = async (
  contractID: number,
  fieldID: number,
  fieldValue: string,
  price: number
) => {
  try {
    const result = await postData<Request>({
      path: 'contractFieldRevenueRules/create.do',
      payload: {
        contractID,
        fieldID,
        fieldValue,
        price,
      },
    });

    if (result.success) {
      return result;
    }
  } catch (error: any) {
    console.error('Error fetching data:', error.message);
  }
};

// LEAD RULES - UPDATE CONTRACT FIELD REVENUE RULES
export const updateContractLeadFieldRevenueRules = async (
  fieldRevenueRuleID: number,
  fieldID: number,
  fieldValue: string,
  price: number
) => {
  try {
    const result = await postData<Request>({
      path: 'contractFieldRevenueRules/update.do',
      payload: {
        fieldRevenueRuleID,
        fieldID,
        fieldValue,
        price,
      },
    });

    if (result.success) {
      return result;
    }
  } catch (error: any) {
    console.error('Error fetching data:', error.message);
  }
};

// CALL RULES - GET CONTRACT FIELD REVENUE RULES
export const fetchContractCallFieldRevenueRules = async (
  contractId: number
) => {
  try {
    const result = await getData<Request>({
      path: 'contractCallFieldRevenueRules/getAll.do',
      payload: {
        contractID: contractId,
      },
    });

    if (result.success) {
      return result.response.data;
    }
  } catch (error: any) {
    console.error('Error fetching data:', error.message);
  }
};

// CALL RULES - CREATE CONTRACT FIELD REVENUE RULES
export const createContractCallFieldRevenueRules = async (
  contractID: number,
  fieldID: number,
  fieldValue: string,
  price: number
) => {
  try {
    const result = await postData<Request>({
      path: 'contractCallFieldRevenueRules/create.do',
      payload: {
        contractID,
        fieldID,
        fieldValue,
        price,
      },
    });

    if (result.success) {
      return result;
    }
  } catch (error: any) {
    console.error('Error fetching data:', error.message);
  }
};

// CALL RULES - UPDATE CONTRACT FIELD REVENUE RULES
export const updateContractCallFieldRevenueRules = async (
  callFieldRevenueID: number,
  fieldID: number,
  fieldValue: string,
  price: number
) => {
  try {
    const result = await postData<Request>({
      path: 'contractCallFieldRevenueRules/update.do',
      payload: {
        callFieldRevenueID,
        fieldID,
        fieldValue,
        price,
      },
    });

    if (result.success) {
      return result;
    }
  } catch (error: any) {
    console.error('Error fetching data:', error.message);
  }
};

// LEAD ROUTING RULES - GET ALL (Toggle)
export const fetchLeadRules = async (
  advertiserID?: string,
  affiliateID?: string
) => {
  try {
    const result = await getData<Request>({
      path: 'leadRoutingRules/getAll.do',
      payload: {
        ...(advertiserID && { advertiserID }),
        ...(affiliateID && { affiliateID }),
        status: 'Active',
        start: 0,
        limit: 1000,
      },
    });

    if (result.success) {
      return result.response.data;
    }
  } catch (error: any) {
    console.error('Error fetching data:', error.message);
  }
};

// LEAD ROUTING RULES - CREATE (Toggle)
export const createLeadRoutingRule = async (
  advertiserID: number,
  campaignID: number,
  contractID: number,
  affiliateID: string
) => {
  try {
    const result = await postData<Request>({
      path: 'leadRoutingRules/create.do',
      payload: {
        advertiserID,
        affiliateID,
        campaignID,
        contractID,
        rule: 'Block',
      },
    });

    if (result.success) {
      return result;
    }
  } catch (error: any) {
    console.error('Error fetching data:', error.message);
  }
};

// LEAD ROUTING RULES - DELETE (Toggle)
export const deleteLeadRoutingRule = async (leadRoutingID: number) => {
  try {
    const result = await postData<Request>({
      path: 'leadRoutingRules/delete.do',
      payload: {
        leadRoutingID,
      },
    });

    if (result.success) {
      return result;
    }
  } catch (error: any) {
    console.error('Error fetching data:', error.message);
  }
};

// CALL ROUTING RULES - GET ALL (Toggle)
export const fetchCallRules = async (
  advertiserID?: string,
  affiliateID?: string
) => {
  try {
    const result = await getData<Request>({
      path: 'callRoutingRules/getAll.do',
      payload: {
        ...(advertiserID && { advertiserID }),
        ...(affiliateID && { affiliateID }),
        status: 'Active',
        start: 0,
        limit: 1000,
      },
    });

    if (result.success) {
      return result.response.data;
    }
  } catch (error: any) {
    console.error('Error fetching data:', error.message);
  }
};

// CALL ROUTING RULES - CREATE (Toggle)
export const createCallRoutingRule = async (
  advertiserID: number,
  campaignID: number,
  contractID: number,
  affiliateID: string
) => {
  try {
    const result = await postData<Request>({
      path: 'callRoutingRules/create.do',
      payload: {
        advertiserID,
        affiliateID,
        campaignID,
        contractID,
        rule: 'Block',
      },
    });

    if (result.success) {
      return result;
    }
  } catch (error: any) {
    console.error('Error fetching data:', error.message);
  }
};

// CALL ROUTING RULES - DELETE (Toggle)
export const deleteCallRoutingRule = async (callRoutingRuleID: number) => {
  try {
    const result = await postData<Request>({
      path: 'callRoutingRules/delete.do',
      payload: {
        callRoutingRuleID,
      },
    });

    if (result.success) {
      return result;
    }
  } catch (error: any) {
    console.error('Error fetching data:', error.message);
  }
};
// CAPS AFFILIATE -FETCH
export const fetchCaps = async (contractId: number) => {
  try {
    const result = await getData<Request>({
      path: 'leadDistributionContracts/getAffiliateCaps.do',
      payload: {
        contractID: contractId,
        limit: 999,
      },
    });

    if (result.success) {
      return result.response.data;
    }
  } catch (error: any) {
    console.error('Error fetching caps data:', error.message);
  }
};

// CAPS AFFILIATE - CREATE
export const createCap = async (
  affiliateID: number,
  campaignID: number,
  cap: number,
  contractID: number
) => {
  if (cap === 0) {
    cap = NaN;
  }

  try {
    const result = await postData<Request>({
      path: 'leadDistributionContracts/createAffiliateCap.do',
      payload: {
        affiliateID,
        campaignID,
        cap,
        contractID,
        period: 'Weekly',
      },
    });

    if (result.success) {
      return result.data;
    }
  } catch (error: any) {
    console.error('Error creating cap data:', error.message);
  }
};

// CAPS AFFILIATE - DELETE
export const deleteCap = async (contractAffiliateCapID: number) => {
  try {
    const result = await postData<Request>({
      path: 'leadDistributionContracts/deleteAffiliateCap.do',
      payload: {
        contractAffiliateCapID,
      },
    });

    if (result.success) {
      return result.success;
    }
  } catch (error: any) {
    console.error('Error creating cap data:', error.message);
  }
};

// ZIPS
export const fetchCallZips = async () => {
  try {
    const result = await getData<Request>({
      path: 'callRoutingContracts/getZipCodesTargeting.do',
    });

    if (result.success) {
      return result.response.data;
    }
  } catch (error: any) {
    console.error('Error fetching data:', error.message);
  }
};

// LEAD FILTERS
export const fetchLeadFilters = async (contractId?: number) => {
  try {
    const result = await getData<Request>({
      path: 'leadDistributionContracts/getFilters.do',
      payload: {
        ...(contractId && { contractID: contractId }),
        limit: 1000,
        start: 0,
      },
    });

    if (result.success) {
      return result.response.data;
    }
  } catch (error: any) {
    console.error('Error fetching data:', error.message);
  }
};

// BUNDLE OFFERS
export const changeStatus = async (
  contractId: number,
  status: string,
  campaignType: string
) => {
  let path = 'leadDistributionContracts/changeStatus.do';
  if (campaignType === 'calls') {
    path = 'callRoutingContracts/changeStatus.do';
  }
  try {
    const result = await postData<Request>({
      path: path,
      payload: {
        contractID: contractId,
        status,
      },
    });

    if (result.success) {
      return result;
    }
  } catch (error: any) {
    console.error('Error fetching data:', error.message);
  }
};

// LEADS CAP - FETCH
export const fetchLeadsCap = async (contractId: number) => {
  try {
    const result = await getData<Request>({
      path: 'leadDistributionContracts/getLeadsCapInfo.do',
      payload: {
        contractID: contractId,
      },
    });
    if (result.success) {
      return result.data;
    }
  } catch (error: any) {
    console.error('Error fetching data:', error.message);
  }
};

// LEADS CAP - UPDATE
export const updateLeadsCap = async (contractID: number, leadsCap: number) => {
  try {
    const result = await postData<Request>({
      path: 'leadDistributionContracts/updateLeadsCap.do',
      payload: {
        contractID,
        weeklyLeadsCap: leadsCap,
      },
    });
    if (result.success) {
      return result;
    }
  } catch (error: any) {
    console.error('Error fetching data:', error.message);
  }
};

// REVENUE MODEL - UPDATE
export const updateRevenueModel = async (
  contractID: number,
  revenueModel: string,
  duration: string,
  price: number
) => {
  try {
    const result = await postData<Request>({
      path: 'callRoutingContracts/updateRevenueModel.do',
      payload: {
        contractID,
        revenueModel,
        duration,
        price,
      },
    });

    if (result.success) {
      return result;
    }
  } catch (error: any) {
    console.error('Error updating revenue model:', error.message);
  }
};
